/**
 * ブラウザ標準の alert / confirm の代わりに使う、アプリ共通ダイアログ。
 */

export type AppDialogOptions = {
  title?: string;
  okText?: string;
  cancelText?: string;
};

type DialogMode = 'alert' | 'confirm';

let host: HTMLDialogElement | null = null;
let titleEl: HTMLElement | null = null;
let bodyEl: HTMLElement | null = null;
let okBtn: HTMLButtonElement | null = null;
let cancelBtn: HTMLButtonElement | null = null;
let resolveFn: ((value: boolean) => void) | null = null;

function ensureHost(): HTMLDialogElement {
  if (host && document.body.contains(host)) return host;

  host = document.createElement('dialog');
  host.className = 'app-dialog';
  host.setAttribute('aria-modal', 'true');
  host.innerHTML = `
    <div class="app-dialog-inner">
      <header class="app-dialog-header">
        <h2 class="app-dialog-title" id="app-dialog-title"></h2>
      </header>
      <div class="app-dialog-body" id="app-dialog-body"></div>
      <footer class="app-dialog-actions">
        <button type="button" class="app-dialog-btn app-dialog-btn--ghost" data-app-dialog="cancel">キャンセル</button>
        <button type="button" class="app-dialog-btn app-dialog-btn--primary" data-app-dialog="ok">OK</button>
      </footer>
    </div>
  `;
  document.body.appendChild(host);

  titleEl = host.querySelector('#app-dialog-title');
  bodyEl = host.querySelector('#app-dialog-body');
  okBtn = host.querySelector('[data-app-dialog="ok"]');
  cancelBtn = host.querySelector('[data-app-dialog="cancel"]');

  const finish = (value: boolean): void => {
    const r = resolveFn;
    resolveFn = null;
    if (host?.open) host.close();
    r?.(value);
  };

  okBtn?.addEventListener('click', () => finish(true));
  cancelBtn?.addEventListener('click', () => finish(false));
  host.addEventListener('cancel', (e) => {
    e.preventDefault();
    finish(false);
  });
  host.addEventListener('click', (e) => {
    if (e.target === host) finish(false);
  });

  return host;
}

function openDialog(
  mode: DialogMode,
  message: string,
  options: AppDialogOptions | undefined,
  defaults: { title: string; okText: string }
): Promise<boolean> {
  const d = ensureHost();
  const title = options?.title ?? defaults.title;
  const okText = options?.okText ?? defaults.okText;
  const cancelText = options?.cancelText ?? 'キャンセル';

  if (titleEl) titleEl.textContent = title;
  if (bodyEl) bodyEl.textContent = message;
  if (okBtn) okBtn.textContent = okText;
  if (cancelBtn) {
    cancelBtn.textContent = cancelText;
    cancelBtn.hidden = mode === 'alert';
  }
  d.setAttribute('aria-labelledby', 'app-dialog-title');

  return new Promise((resolve) => {
    resolveFn = resolve;
    d.showModal();
    window.requestAnimationFrame(() => okBtn?.focus());
  });
}

export function appConfirm(message: string, options?: AppDialogOptions): Promise<boolean> {
  return openDialog('confirm', message, options, {
    title: '確認',
    okText: 'OK'
  });
}

export async function appAlert(message: string, options?: AppDialogOptions): Promise<void> {
  await openDialog('alert', message, options, {
    title: 'お知らせ',
    okText: '閉じる'
  });
}
